import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { constructNameEN } from '@/employee/employee-name-helper';
import { handleMutationResult } from '@/helpers/toastHelper';
import {
  useDeleteLeaveTypesMutation,
  useGetEmployeesByIdsQuery,
  useGetLocationsQuery,
  useLeaveTypeDeleteValidationQuery
} from '@/services/gql/graphql.generated';
import { LeaveType } from '@/services/leaveManagement/leave.types';
import { useRefreshLeaveTypes } from '@/services/leaveManagement/LeaveTypesProvider';
import { createErrorToast } from '@/services/notification/ToastService';
import { format } from 'date-fns';
import { FC, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import strings from '../admin.translations';

interface LeaveTypeDeleteDialogProps {
  leaveType: LeaveType;
  open: boolean;
  onClose: () => void;
}

export const AdminLeaveTypeDeleteDialog: FC<LeaveTypeDeleteDialogProps> = ({ leaveType, open, onClose }) => {
  const boundaryDate = format(new Date(), 'yyyy-MM-dd');
  const [{ fetching: deletingLeaveType }, deleteLeaveTypes] = useDeleteLeaveTypesMutation();
  // Start validation after the dialog opens. Auto-starting this query during the open render caused
  // a visible background repaint, so we keep it paused and reexecute it once the modal is mounted.
  const [
    { data: deleteValidationData, fetching: deleteValidationLoading, error: deleteValidationError },
    reexecuteDeleteValidation
  ] = useLeaveTypeDeleteValidationQuery({
    variables: {
      leaveTypeId: leaveType.id,
      boundaryDate
    },
    pause: true,
    requestPolicy: 'network-only'
  });

  useEffect(() => {
    if (!open || !leaveType) {
      return;
    }

    reexecuteDeleteValidation({ requestPolicy: 'network-only' });
  }, [leaveType, open, reexecuteDeleteValidation]);

  useEffect(() => {
    if (!deleteValidationError || !open) {
      return;
    }

    createErrorToast(strings.leaveTypeSection.deleteLeaveTypeDialog.validationError);
    onClose();
  }, [deleteValidationError, onClose, open]);

  const navigate = useNavigate();

  const refetch = useRefreshLeaveTypes();

  const handleDelete = async () => {
    if (!leaveType || deleteValidationLoading || hasUsagesOnOrAfterBoundary) {
      return;
    }

    const result = await deleteLeaveTypes({ ids: [leaveType.id] });

    await refetch({ requestPolicy: 'network-only' });
    setTimeout(
      () =>
        handleMutationResult(result, {
          successMessage: strings.leaveTypeSection.deleteLeaveTypeDialog.deleteSuccess,
          errorMessage: strings.leaveTypeSection.deleteLeaveTypeDialog.deleteError,
          customSuccessCheck: data => data?.deleteLeaveTypes === true,
          onSuccess: async () => {
            onClose();
            navigate('/admin/leave-types');
          }
        }),
      100
    );
  };

  const hasUsagesBeforeBoundary = deleteValidationData?.leaveTypeDeleteValidation.hasUsagesBeforeBoundary ?? false;
  const usagesOnOrAfterBoundary = deleteValidationData?.leaveTypeDeleteValidation.usagesOnOrAfterBoundary ?? [];
  const hasUsagesOnOrAfterBoundary = usagesOnOrAfterBoundary.length > 0;
  const employeeIds = useMemo(
    () => Array.from(new Set(usagesOnOrAfterBoundary.map(usage => usage.employeeId))),
    [usagesOnOrAfterBoundary]
  );
  const [{ data: employeesByIdsData }] = useGetEmployeesByIdsQuery({
    variables: { ids: employeeIds },
    pause: !open || employeeIds.length === 0
  });
  const [{ data: locationsData }] = useGetLocationsQuery({
    pause: !open || usagesOnOrAfterBoundary.length === 0
  });
  const employeeNameById = useMemo(() => {
    const employees = employeesByIdsData?.employeesByIds ?? [];
    return new Map(
      employees
        .filter((employee): employee is NonNullable<typeof employee> => Boolean(employee?.identityId))
        .map(employee => [
          employee.identityId!,
          constructNameEN(employee.firstName, employee.lastName ?? '').trim() ||
            strings.leaveTypeSection.deleteLeaveTypeDialog.unknownEmployee
        ])
    );
  }, [employeesByIdsData]);
  const locationNameById = useMemo(() => {
    const locations = locationsData?.locations ?? [];
    return new Map(
      locations
        .filter((location): location is NonNullable<typeof location> => Boolean(location))
        .map(location => [location.id, location.name])
    );
  }, [locationsData]);

  const title = leaveType
    ? strings.leaveTypeSection.deleteLeaveTypeDialog.title(leaveType.properties.displayName || leaveType.name)
    : undefined;

  const formatUsageDate = (date: string) => new Date(`${date}T00:00:00`).toLocaleDateString();

  const body = deleteValidationLoading ? (
    <p className="text-sm text-secondary-std">{strings.leaveTypeSection.deleteLeaveTypeDialog.loading}</p>
  ) : hasUsagesOnOrAfterBoundary ? (
    <div className="space-y-3">
      <p className="text-sm text-primary-std">{strings.leaveTypeSection.deleteLeaveTypeDialog.futureMessage}</p>
      <div>
        <p className="text-sm font-medium text-primary-std">
          {strings.leaveTypeSection.deleteLeaveTypeDialog.futureListHeading}
        </p>
        <ul className="mt-2 max-h-72 space-y-2 overflow-y-auto rounded border border-primary-std p-3 text-sm text-primary-std">
          {usagesOnOrAfterBoundary.map(usage => (
            <li key={usage.availabilityExceptionId}>
              {[
                formatUsageDate(usage.date),
                usage.locationId != null ? locationNameById.get(usage.locationId) : null,
                employeeNameById.get(usage.employeeId) ?? strings.leaveTypeSection.deleteLeaveTypeDialog.unknownEmployee
              ]
                .filter(Boolean)
                .join(' ')}
            </li>
          ))}
        </ul>
      </div>
    </div>
  ) : hasUsagesBeforeBoundary ? (
    <p className="text-sm text-primary-std">{strings.leaveTypeSection.deleteLeaveTypeDialog.pastMessage}</p>
  ) : (
    <p className="text-sm text-primary-std">{strings.leaveTypeSection.deleteLeaveTypeDialog.message}</p>
  );

  return (
    <ConfirmDialog
      title={title}
      confirmLabel={strings.leaveTypeSection.deleteLeaveTypeDialog.confirm}
      cancelLabel={strings.common.cancel}
      dangerous
      loading={deletingLeaveType}
      confirmDisabled={deleteValidationLoading || hasUsagesOnOrAfterBoundary}
      open={open}
      onClose={onClose}
      onConfirm={handleDelete}
    >
      {body}
    </ConfirmDialog>
  );
};
